import { Clock, MapPin } from 'lucide-react';
import type { Presentation, Criterion } from '../../shared/types';
import { computeTotal, computeMaxScore, isWeighted, scoreBand } from '../../shared/scoring';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

export interface Draft {
  values: Record<string, number>;
  comments: string;
}

export function ScoreCard({
  presentation,
  criteria,
  draft,
  onSetScore,
  onSetComments,
}: {
  presentation: Presentation;
  criteria: Criterion[];
  draft: Draft;
  onSetScore: (criterionId: string, value: number) => void;
  onSetComments: (text: string) => void;
}) {
  const total = computeTotal(criteria, draft.values);
  const maxScore = computeMaxScore(criteria);
  const weighted = isWeighted(criteria);
  const band = scoreBand(weighted ? total : (total / maxScore) * 5);
  const scored = criteria.filter((c) => draft.values[c.id]).length;

  return (
    <div className="space-y-5">
      {/* meta */}
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge variant={presentation.type === 'oral' ? 'oral' : 'poster'} className="capitalize">
            {presentation.type}
          </Badge>
          <span className="text-xs font-semibold text-muted-foreground">{presentation.code ?? presentation.id}</span>
          {presentation.category && <Badge variant="secondary">{presentation.category}</Badge>}
        </div>
        <h2 className="text-lg font-bold leading-snug tracking-tight">{presentation.title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{presentation.presenterName}</p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {presentation.location && (
            <span className="inline-flex items-center gap-1 font-semibold text-foreground">
              <MapPin className="size-3.5 text-primary" /> {presentation.location}
            </span>
          )}
          {presentation.timeSlot && (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" /> {presentation.timeSlot}
            </span>
          )}
          {presentation.boardNumber && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="size-3.5" /> Board row {presentation.boardNumber}
            </span>
          )}
          {presentation.displayTime && (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" /> {presentation.displayTime}
            </span>
          )}
        </div>
      </div>

      {/* criteria */}
      <div className="space-y-3">
        {criteria.map((c) => {
          const current = draft.values[c.id] || 0;
          return (
            <div key={c.id} className="rounded-lg border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{c.label}</div>
                  <p className="mt-0.5 text-sm text-muted-foreground">{c.description}</p>
                </div>
                {typeof c.weight === 'number' && (
                  <Badge variant="secondary" className="shrink-0">
                    {Math.round(c.weight * 100)}%
                  </Badge>
                )}
              </div>
              <div className="mt-3 grid grid-cols-5 gap-2">
                {Array.from({ length: c.maxScore }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => onSetScore(c.id, n)}
                    aria-pressed={current === n}
                    className={cn(
                      'flex h-11 items-center justify-center rounded-md border text-base font-bold transition-all',
                      current === n
                        ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                        : 'border-input bg-background hover:border-primary hover:bg-accent',
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* total */}
      <div className="flex items-center justify-between rounded-lg border bg-card p-4">
        <div>
          <div className="text-sm text-muted-foreground">{weighted ? 'Weighted total' : 'Total'}</div>
          {scored === criteria.length ? (
            <div className="text-sm font-semibold" style={{ color: band.color }}>
              {band.label}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {scored}/{criteria.length} criteria scored
            </div>
          )}
        </div>
        <div className="text-3xl font-bold tabular-nums">
          {total}
          <span className="text-lg font-semibold text-muted-foreground">/{maxScore}</span>
        </div>
      </div>

      {/* comments */}
      <div>
        <label className="mb-1.5 block text-sm font-medium text-muted-foreground">Comments (optional)</label>
        <textarea
          value={draft.comments}
          onChange={(e) => onSetComments(e.target.value)}
          rows={3}
          placeholder="Any notes on this presentation…"
          className="w-full resize-y rounded-md border border-input bg-card p-3 text-sm shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        />
      </div>
    </div>
  );
}
